import { useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { usePurchaseReporting } from '../hooks/usePurchaseReporting';
import { OverviewTab } from '../components/purchase-reporting/OverviewTab';
import { ByVendorTab } from '../components/purchase-reporting/ByVendorTab';
import { ByProductTab } from '../components/purchase-reporting/ByProductTab';
import { DetailTab } from '../components/purchase-reporting/DetailTab';
import type { PRTab } from '../types/purchaseReporting';

const TABS: { key: PRTab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'by-vendor', label: 'By Vendor' },
    { key: 'by-product', label: 'By Product' },
    { key: 'detail', label: 'Detail' },
];

function fmtDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function getPeriodDates(preset: string, year: number): { from: string; to: string } {
    const now = new Date();
    const lastCompleteMonth = year < now.getFullYear() ? 12 : now.getMonth();

    switch (preset) {
        case 'YTD': {
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: `${year}-01-01`, to: fmtDate(end) };
        }
        case 'MTD': {
            const start = new Date(year, lastCompleteMonth - 1, 1);
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: fmtDate(start), to: fmtDate(end) };
        }
        case '3M': {
            const start = new Date(year, lastCompleteMonth - 3, 1);
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: fmtDate(start), to: fmtDate(end) };
        }
        default: {
            const end = new Date(year, lastCompleteMonth, 0);
            return { from: `${year}-01-01`, to: fmtDate(end) };
        }
    }
}

function detectPeriod(dateFrom: string, dateTo: string, year: number): string {
    for (const preset of ['YTD', 'MTD', '3M']) {
        const dates = getPeriodDates(preset, year);
        if (dates.from === dateFrom && dates.to === dateTo) return preset;
    }
    return '';
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

export function PurchaseReportingPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/purchase-reporting');
    const { filterOptions, filters, updateFilters, loading, error } = usePurchaseReporting();
    const [activeTab, setActiveTab] = useState<PRTab>('overview');
    const [refreshKey, setRefreshKey] = useState(0);
    const savedYear = (() => { try { const v = sessionStorage.getItem('pr-year'); return v ? parseInt(v, 10) : currentYear; } catch { return currentYear; } })();
    const [selectedYear, setSelectedYear] = useState(savedYear);
    const [activePeriod, setActivePeriod] = useState<string>(() => detectPeriod(filters.dateFrom, filters.dateTo, savedYear));

    const handleYearChange = useCallback((year: number) => {
        setSelectedYear(year);
        try { sessionStorage.setItem('pr-year', String(year)); } catch { /* ignore */ }
        if (activePeriod) {
            const dates = getPeriodDates(activePeriod, year);
            updateFilters({ dateFrom: dates.from, dateTo: dates.to });
        }
    }, [activePeriod, updateFilters]);

    const handlePeriodClick = useCallback((preset: string) => {
        setActivePeriod(preset);
        const dates = getPeriodDates(preset, selectedYear);
        updateFilters({ dateFrom: dates.from, dateTo: dates.to });
    }, [selectedYear, updateFilters]);

    const handleDateChange = useCallback((field: 'dateFrom' | 'dateTo', value: string) => {
        const newFrom = field === 'dateFrom' ? value : filters.dateFrom;
        const newTo = field === 'dateTo' ? value : filters.dateTo;
        setActivePeriod(detectPeriod(newFrom, newTo, selectedYear));
        updateFilters({ [field]: value });
    }, [updateFilters, filters.dateFrom, filters.dateTo, selectedYear]);

    const handleNavigateToDetail = useCallback((filterUpdate: Partial<typeof filters>) => {
        updateFilters(filterUpdate);
        setActiveTab('detail');
    }, [updateFilters]);

    if (permLoading || loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900">Purchase Reporting</h1>
                    {canEdit && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                            Edit Access
                        </span>
                    )}
                </div>
                <button
                    onClick={() => setRefreshKey(k => k + 1)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border rounded-md hover:bg-gray-50"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* Filter Bar */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex flex-wrap items-end gap-4">
                    {/* Company */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Company</label>
                        <select
                            value={filters.company}
                            onChange={e => updateFilters({ company: e.target.value })}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                        >
                            <option value="">All Companies</option>
                            {filterOptions.companies.map(c => (
                                <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                            ))}
                        </select>
                    </div>

                    {/* Year */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Year</label>
                        <select
                            value={selectedYear}
                            onChange={e => handleYearChange(parseInt(e.target.value, 10))}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                        >
                            {YEARS.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    {/* Period Presets */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Period</label>
                        <div className="flex border rounded-md overflow-hidden">
                            {['YTD', 'MTD', '3M'].map(p => (
                                <button
                                    key={p}
                                    onClick={() => handlePeriodClick(p)}
                                    className={`px-3 py-1.5 text-sm ${
                                        activePeriod === p
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {p === '3M' ? '3 Months' : p}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date From */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">From</label>
                        <input
                            type="date"
                            value={filters.dateFrom}
                            onChange={e => handleDateChange('dateFrom', e.target.value)}
                            className="border rounded-md px-3 py-1.5 text-sm"
                        />
                    </div>

                    {/* Date To */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">To</label>
                        <input
                            type="date"
                            value={filters.dateTo}
                            onChange={e => handleDateChange('dateTo', e.target.value)}
                            className="border rounded-md px-3 py-1.5 text-sm"
                        />
                    </div>

                    {/* Business Unit */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Business Unit</label>
                        <select
                            value={filters.businessUnit}
                            onChange={e => updateFilters({ businessUnit: e.target.value })}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                        >
                            <option value="">All</option>
                            {filterOptions.businessUnits.map(bu => (
                                <option key={bu} value={bu}>{bu}</option>
                            ))}
                        </select>
                    </div>

                    {/* Vendor */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Vendor</label>
                        <select
                            value={filters.vendorID}
                            onChange={e => updateFilters({ vendorID: e.target.value })}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                        >
                            <option value="">All Vendors</option>
                            {filterOptions.vendors.map(v => (
                                <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
                            ))}
                        </select>
                    </div>

                    {/* Warehouse */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Warehouse</label>
                        <select
                            value={filters.locationID}
                            onChange={e => updateFilters({ locationID: e.target.value })}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                        >
                            <option value="">All Warehouses</option>
                            {filterOptions.warehouses.map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b">
                    <nav className="flex -mb-px">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === tab.key
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-4">
                    {activeTab === 'overview' && (
                        <OverviewTab
                            filters={filters}
                            selectedYear={selectedYear}
                            refreshKey={refreshKey}
                        />
                    )}
                    {activeTab === 'by-vendor' && (
                        <ByVendorTab
                            filters={filters}
                            refreshKey={refreshKey}
                            onNavigateToDetail={handleNavigateToDetail}
                        />
                    )}
                    {activeTab === 'by-product' && (
                        <ByProductTab
                            filters={filters}
                            refreshKey={refreshKey}
                            onNavigateToDetail={handleNavigateToDetail}
                        />
                    )}
                    {activeTab === 'detail' && (
                        <DetailTab
                            filters={filters}
                            refreshKey={refreshKey}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
